import React, {useCallback, useRef, useState} from 'react';
import {useLoaderData} from "react-router";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import natureService from "../../../../../services/web/nature-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaPen, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import ecapService from "../../../../../services/web/ecap-service.js";
import {FaArrowUpRightFromSquare, FaFilePdf, FaPencil} from "react-icons/fa6";
import EcapTemplateModal from "./components/ecap-template-modal.jsx";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const initialFilters = {
    dtIni: "",
    dtEnd: "",
    status: "",
    nature: [],
    description: "",
}

const EcapList = () => {
    const loaderData = useLoaderData();
    const navigate = navigateHook();
    const hasPermission = useHasPermission();

    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState([]);
    const [filterValues, setFilterValues] = useState(initialFilters);

    const {data} = useQuery({
        queryFn: () => ecapService.list(filter),
        queryKey: ["list", {filter}]
    });

    const {data: reasons} = useQuery({
        queryFn: () => natureService.getAllToSelect(),
        queryKey: ["reasons"]
    });

    const [columnDefs,] = useState([
        { field: "ecap", headerName: loaderData?.ecap_acronym, width: 150, comparator: (a, b) => aggridUtils.agGridNumberYearComparator(a, b)},
        { field: "incident_datetime", headerName: "Data/Hora do fato", flex: 1, valueFormatter: ({value}) => projectUtils.formatDate(value),comparator: aggridUtils.nullLastComparator},
        { field: "applicant", headerName: "Solicitante", flex: 1,comparator: aggridUtils.nullLastComparator},
        { field: "description", headerName: "Descrição", flex: 1,comparator: aggridUtils.nullLastComparator},
        { field: "reason", headerName: "Natureza", flex: 1,comparator: aggridUtils.nullLastComparator},
        { field: "created_by", headerName: "Criado por", flex: 1,comparator: aggridUtils.nullLastComparator},
        { field: "status", headerName: "Status", flex: 1,comparator: aggridUtils.nullLastComparator},
        {
            field: "",
            sortable: false,
            headerName: "Ações",
            cellRenderer: ({data: {last_ged_id, ecap, solicitation_id}}) => (
                <div className={"flex gap-2 h-full w-full justify-center items-center"}>
                    <Button className={"!h-[25px]"} title={"Abrir solicitação"} onClick={() => navigate(`/ecap-solicitation/form/${solicitation_id}`, true)}>
                        <FaArrowUpRightFromSquare />
                    </Button>
                    <Button className={"!h-[25px]"} title={"Editar"} onClick={() => navigate(`/ecap/form/${ecap.replace("/", "-")}`)}>
                        <FaPen />
                    </Button>
                    <Button className={"!h-[25px]"} title={"Abrir o último PDF gerado"} disabled={!last_ged_id} onClick={() => window.open(`${API_KEEPER}/ged/attach/print/pdf/${last_ged_id}`, last_ged_id)}>
                        <FaFilePdf />
                    </Button>
                </div>
            )
        }
    ]);

    const gridRef = useRef(undefined);

    const apply = useCallback(() => {
        setFilter([
            {field: "dateBetween", type: "between", value: {dtIni: filterValues.dtIni, dtEnd: filterValues.dtEnd}},
            {field: "status", type: "like", value: filterValues.status},
            {field: "nature", type: "like", value: filterValues.nature.map(d => d.value).join(",")},
            {field: "description", type: "like", value: filterValues.description},
        ]);
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, [])

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <>
            <Template>
                <Template.Header>
                    <Template.Title>{loaderData?.ecap_acronym}</Template.Title>

                    {
                        hasPermission(PermissionEnum.ECAP_TEMPLATE) &&
                        <Button target={"primary"} onClick={() => setOpen(true)}>
                            Modelos
                        </Button>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className={"col-span-6 flex items-stretch"}>
                                    <div className="relative flex-1">
                                        <InputDateTime
                                            id={"dtIni"}
                                            selected={filterValues.dtIni}
                                            label={"Data/Hora do fato entre"}
                                            onChange={(date) => setFilterValues(prev => ({...prev, dtIni: date }))}
                                            customIcon
                                            classNameWrapper={"right-date-input"}
                                            dateFormat={"LL"}
                                        />
                                    </div>

                                    <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                    <div className="relative flex-1">
                                        <InputDateTime
                                            id={"dtEnd"}
                                            selected={filterValues.dtEnd}
                                            label={""}
                                            onChange={(date) => setFilterValues(prev => ({...prev, dtEnd: date }))}
                                            classNameWrapper={"left-date-input"}
                                            dateFormat={"LL"}
                                        />
                                    </div>
                                </div>

                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    value={filterValues.status}
                                    options={[
                                        {value: "PENDING", label: "Pendente"},
                                        {value: "FINALIZED", label: "Finalizada com " + loaderData?.ecap_acronym},
                                        {value: "FINALIZED_WITHOUT_ECAP", label: "Finalizada sem " + loaderData?.ecap_acronym}
                                    ]}
                                    label={"Status"}
                                    onChange={(value) => setFilterValues(prev => ({...prev, status: value?.value}))}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    value={filterValues.nature}
                                    options={reasons ?? []}
                                    label={"Naturezas"}
                                    onChange={(value) => setFilterValues(prev => ({...prev, nature: value}))}
                                    isMulti
                                />

                                <div className="col-span-12 flex justify-between gap-4">
                                    <InputForm
                                        classNameWrapper={"w-full"}
                                        id={"description"}
                                        label={"Descrição"}
                                        value={filterValues.description}
                                        onChange={({target: {value}}) => setFilterValues(prev => ({...prev, description: value}))}
                                    />

                                    <div className={"flex gap-2"}>
                                        <Button target={"default"} onClick={clear} title={"Limpar"}>
                                            <FaEraser />
                                        </Button>
                                        <Button onClick={apply} title={"Buscar"}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>

                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <div className={"aggrid-row-pointer"} style={{height: "calc(100vh - 201px)"}}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data ?? []}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme()}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <EcapTemplateModal
                open={open}
                setOpen={setOpen}
            />
        </>
    );
};

export default EcapList;